import { Controller } from '@hotwired/stimulus'
import bootstrap from 'bootstrap/dist/js/bootstrap'

// Runs a free Lighthouse (PageSpeed Insights) audit on a visitor-entered URL.
// On submit we immediately open a modal with a loading indicator, then swap its
// content for the four category scores rendered as animated rings, then funnel to
// a demo.
//
// The modal lives outside this controller's element (at the page top level, away
// from the hero's `will-change: transform` ancestor that would break a fixed
// modal), so we reference it by id — matching the app's existing modal pattern —
// rather than via Stimulus targets.
//
// The numeric count-up inside each ring is handled by reusing the existing
// `count-up` controller — we inject spans with its data attributes and Stimulus
// connects them automatically.
export default class extends Controller {
  static targets = ['input', 'submit', 'label', 'icon']
  static values = {
    url: String,
    demoUrl: String,
    modalId: String
  }

  static CATEGORIES = [
    { key: 'performance', label: 'Performance' },
    { key: 'accessibility', label: 'Accessibility' },
    { key: 'best_practices', label: 'Best Practices' },
    { key: 'seo', label: 'SEO' }
  ]

  submit(event) {
    event.preventDefault()
    if (this.loading) return

    const url = this.inputTarget.value.trim()
    if (!url) {
      this.inputTarget.focus()
      return
    }

    if (!this.looksLikeDomain(url)) {
      this.inputTarget.setCustomValidity('Please enter a valid website address, like yourrestaurant.com')
      this.inputTarget.reportValidity()
      this.inputTarget.focus()
      return
    }

    if (window.gtag) {
      window.gtag('event', 'site_audit_submit', {
        event_category: 'engagement',
        event_label: url
      })
    }

    this.startLoading()

    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      credentials: 'same-origin',
      body: JSON.stringify({ url })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, status: response.status, data })))
      .then(({ ok, status, data }) => {
        if (ok) {
          this.renderResult(data)
        } else if (status === 429) {
          this.showError(data.error || 'Too many requests. Please try again shortly.')
        } else {
          this.showError(data.error || "We couldn't analyze that site. Please try again.")
        }
      })
      .catch(() => {
        this.showError('Something went wrong. Please try again.')
      })
      .finally(() => {
        this.stopLoading()
      })
  }

  connect() {
    this.modalElement.addEventListener('hidden.bs.modal', this.handleModalHidden)
  }

  disconnect() {
    this.modalElement.removeEventListener('hidden.bs.modal', this.handleModalHidden)
    this.stopLoadingTextCycle()
  }

  handleModalHidden = () => {
    this.stopLoadingTextCycle()
  }

  startLoading() {
    this.loading = true
    this.submitTarget.disabled = true
    this.inputTarget.disabled = true
    this.element.classList.add('is-loading')
    if (this.hasLabelTarget) this.labelTarget.textContent = 'Analyzing'
    this.openModalLoading()
  }

  stopLoading() {
    this.loading = false
    this.submitTarget.disabled = false
    this.inputTarget.disabled = false
    this.element.classList.remove('is-loading')
    if (this.hasLabelTarget) this.labelTarget.textContent = 'Free audit'
    this.stopLoadingTextCycle()
  }

  // --- Modal state transitions ---------------------------------------------

  openModalLoading() {
    this.setModalState('loading')
    this.startLoadingTextCycle()
    this.modal.show()
  }

  startLoadingTextCycle() {
    this.stopLoadingTextCycle()

    let step = 0
    const steps = [
      { title: 'Connecting to audit engine...', sub: 'Reaching Google PageSpeed Insights API' },
      { title: 'Simulating mobile visitor viewport...', sub: 'Emulating modern smartphone device' },
      { title: 'Loading website assets...', sub: 'Measuring Time to First Byte (TTFB)' },
      { title: 'Evaluating render performance...', sub: 'Checking Largest Contentful Paint (LCP)' },
      { title: 'Checking visual stability...', sub: 'Analyzing Cumulative Layout Shift (CLS)' },
      { title: 'Parsing assets...', sub: 'Inspecting image compression & modern formats' },
      { title: 'Auditing accessibility features...', sub: 'Verifying color contrast and ARIA tags' },
      { title: 'Evaluating search visibility...', sub: 'Inspecting meta descriptions and HTML structure' },
      { title: 'Compiling Lighthouse metrics...', sub: 'Aggregating category scores' },
      { title: 'Polishing report...', sub: 'Almost ready to show' }
    ]

    const updateText = () => {
      const info = steps[step]
      if (info) {
        const titleEl = this.modalElement.querySelector('.site-audit-modal__loading-title')
        const subEl = this.modalElement.querySelector('.site-audit-modal__loading-sub')
        
        if (titleEl && subEl) {
          if (step === 0) {
            titleEl.textContent = info.title
            subEl.textContent = info.sub
            step++
          } else {
            titleEl.classList.add('loading-text-transitioning')
            subEl.classList.add('loading-text-transitioning')
            
            setTimeout(() => {
              titleEl.textContent = info.title
              subEl.textContent = info.sub
              titleEl.classList.remove('loading-text-transitioning')
              subEl.classList.remove('loading-text-transitioning')
              step++
            }, 200)
          }
        } else {
          step++
        }
      } else {
        clearInterval(this.loadingInterval)
      }
    }

    updateText()
    this.loadingInterval = setInterval(updateText, 2500)
  }

  stopLoadingTextCycle() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval)
      this.loadingInterval = null
    }
  }

  renderResult(data) {
    const scores = data.scores || {}
    
    if (window.gtag) {
      window.gtag('event', 'site_audit_success', {
        event_category: 'engagement',
        event_label: data.url,
        performance: scores.performance,
        accessibility: scores.accessibility,
        best_practices: scores.best_practices,
        seo: scores.seo
      })
    }

    const rings = this.constructor.CATEGORIES.map(({ key, label }) => {
      const score = Number.isFinite(scores[key]) ? scores[key] : 0
      const band = this.band(score)
      return `
        <div class="site-audit__metric">
          <div class="site-audit__ring site-audit__ring--${band}" style="--pct:${score}">
            <span class="site-audit__ring-value"
                  data-controller="count-up"
                  data-count-up-end-value="${score}"
                  data-count-up-duration-value="1200">0</span>
          </div>
          <span class="site-audit__ring-label">${label}</span>
        </div>`
    }).join('')

    const host = this.displayHost(data.url)
    const guessedBusiness = host.split('.')[0].replace(/^\w/, (c) => c.toUpperCase())
    const demoUrl = `${this.demoUrlValue}${this.demoUrlValue.includes('?') ? '&' : '?'}` +
      `demo_request[website]=${encodeURIComponent(data.url)}` +
      `&demo_request[business]=${encodeURIComponent(guessedBusiness)}` +
      `&demo_request[source]=Free%20Site%20Audit`

    this.modalBlock('result').innerHTML = `
      <p class="site-audit__heading">Lighthouse scores for <strong>${host}</strong></p>
      <div class="site-audit__rings">${rings}</div>
      <div class="site-audit__cta">
        <span>Want us to turn these green?</span>
        <a href="${demoUrl}" class="btn btn-brand">Get a free demo</a>
      </div>`
    this.setModalState('result')
  }

  showError(message) {
    if (window.gtag) {
      window.gtag('event', 'site_audit_failure', {
        event_category: 'engagement',
        event_label: this.inputTarget.value.trim(),
        error_message: message
      })
    }
    this.modalBlock('error').textContent = message
    this.setModalState('error')
  }

  // Toggle which of the loading / result / error blocks is visible.
  setModalState(state) {
    ['loading', 'result', 'error'].forEach((name) => {
      this.modalBlock(name).hidden = name !== state
    })
  }

  // --- Helpers --------------------------------------------------------------

  get modal() {
    if (!this._modal) {
      this._modal = bootstrap.Modal.getOrCreateInstance(this.modalElement)
    }
    return this._modal
  }

  get modalElement() {
    return document.getElementById(this.modalIdValue)
  }

  modalBlock(name) {
    return this.modalElement.querySelector(`[data-audit-block="${name}"]`)
  }

  // Clears the custom validity message once the visitor edits the field, so a
  // prior "invalid address" warning doesn't stick to a now-corrected value.
  clearValidity() {
    this.inputTarget.setCustomValidity('')
  }

  // Mirror of the server's HOSTNAME_REGEX: one or more labels + an alphabetic TLD.
  // Strips an optional scheme and path so "https://site.com/menu" still validates.
  looksLikeDomain(value) {
    const host = value.replace(/^https?:\/\//i, '').split('/')[0]
    return /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(host)
  }

  band(score) {
    if (score >= 90) return 'good'
    if (score >= 50) return 'ok'
    return 'bad'
  }

  displayHost(url) {
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch (e) {
      return url
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
